import { getSelectedDivisionId } from 'app/shared/division/selectedDivisionStorage';
import {
  DecommissionProductOffering,
  DeleteOfferingsRequest,
  DateStrategyRequest,
  DeleteDateStrategyRequest,
  EditPricelistResponse,
  ExcludeProductOffering,
  OfferingPricelistRequest,
  RecommissionProductOffering,
  UpsertOfferingsRequest,
} from "../model/EditPricelist";
import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";
import { ApiErrors } from "./ApiErrors";

const processApiResponse = async (
    response: Response | null,
    defaultErrorMessage: string
): Promise<{ ok: boolean; data: any; errors: string[] }> => {
  if (!response) {
    return { ok: false, data: null, errors: ["No response from server"] };
  }

  let rawText = "";
  try {
    rawText = await response.text();
  } catch {
    rawText = "";
  }

  let data: any = null;
  if (rawText) {
    try {
      data = JSON.parse(rawText);
    } catch {
      data = rawText;
    }
  }

  const errors = ApiErrors.extractAllErrors(data, response, defaultErrorMessage);
  return {
    ok: response.ok && errors.length === 0,
    data,
    errors: response.ok ? [] : errors,
  };
};

const toSchoolIdNumber = (input: unknown): number => {
  if (input === null || input === undefined) return 0;

  if (typeof input === "number") return Number.isFinite(input) ? input : 0;

  if (typeof input === "object") {
    const c: any = input;
    if ("id" in c) return toSchoolIdNumber(c.id);
    if ("Id" in c) return toSchoolIdNumber(c.Id);
    if ("schoolId" in c) return toSchoolIdNumber(c.schoolId);
    if ("SchoolId" in c) return toSchoolIdNumber(c.SchoolId);
  }

  const raw = String(input).trim();
  if (!raw) return 0;

  const stripped = /^School-/i.test(raw) ? raw.replace(/^School-/i, "") : raw;
  const n = Number(stripped);
  return Number.isFinite(n) ? n : 0;
};

const normalizeSchoolId = (v: unknown): { id: number } => ({ id: toSchoolIdNumber(v) });

export async function GetAllActiveOfferings(
    request: OfferingPricelistRequest,
    divisionId?: number
): Promise<{ ok: boolean; data: EditPricelistResponse[]; errorMessage: string }> {
  const localApi = await getLocalApi();

  const resolvedDivisionId =
      typeof divisionId === "number" && divisionId > 0
          ? divisionId
          : getSelectedDivisionId();

  const endpoint = (apiConfig as any).edit_product_offerings_get_all_active.replace(
      "{divisionId}",
      encodeURIComponent(String(resolvedDivisionId))
  );

  const rawSchoolIds = Array.isArray((request as any).schoolIds)
      ? (request as any).schoolIds
      : [];
  const schoolIds = rawSchoolIds
      .map(normalizeSchoolId)
      .filter((s: { id: number }) => Number(s.id) > 0);

  const sanitizedRequest = {
    schoolIds,
    pricingYearId: Number((request as any).pricingYearId) || 0,
    type: (request as any).type,
  };

  const response = await localApi.call(endpoint, "POST", JSON.stringify(sanitizedRequest));

  const result = await processApiResponse(response, "Failed to load pricelist.");
  if (!result.ok) {
    return {
      ok: false,
      data: [],
      errorMessage: result.errors.length ? ApiErrors.formatErrors(result.errors) : "Failed to load pricelist.",
    };
  }

  const payload = result.data;
  const explicitFailure = payload && typeof payload === "object" && payload.isFailed === true;
  if (explicitFailure) {
    const errors = ApiErrors.extractAllErrors(payload, response ?? undefined, "Failed to load pricelist.");
    return {
      ok: false,
      data: [],
      errorMessage: errors.length ? ApiErrors.formatErrors(errors) : "Failed to load pricelist.",
    };
  }

  let pricelist: EditPricelistResponse[] = [];
  if (Array.isArray(payload)) pricelist = payload as EditPricelistResponse[];
  else if (Array.isArray(payload?.value)) pricelist = payload.value as EditPricelistResponse[];
  else if (Array.isArray(payload?.data)) pricelist = payload.data as EditPricelistResponse[];

  return { ok: true, data: pricelist, errorMessage: "" };
}

export async function CreateOfferings(
    request: UpsertOfferingsRequest,
    pricingYear: number,
    divisionId: number
): Promise<{ ok: boolean; message: string; errors?: string[] }> {
  const localApi = await getLocalApi();
  const endpoint = (apiConfig as any).edit_product_offerings_create
      .replace("{divisionId}", divisionId.toString())
      .replace("{pricingYear}", pricingYear.toString());

  const response = await localApi.call(endpoint, "POST", JSON.stringify(request));
  const result = await processApiResponse(response, "Failed to create offerings");

  return {
    ok: result.ok,
    message: result.ok ? "Offerings created successfully" : ApiErrors.formatErrors(result.errors),
    errors: result.ok ? undefined : result.errors,
  };
}

export async function PutPricelistForSchools(
    request: UpsertOfferingsRequest,
    pricingYear: number,
    divisionId: number
): Promise<{ ok: boolean; message: string; errors?: string[] }> {
  const localApi = await getLocalApi();
  const endpoint = apiConfig.edit_product_offerings_update_active
      .replace("{divisionId}", divisionId.toString())
      .replace("{pricingYear}", pricingYear.toString());

  const response = await localApi.call(endpoint, "PUT", JSON.stringify(request));
  const result = await processApiResponse(response, "Failed to save pricelist");

  return {
    ok: result.ok,
    message: result.ok ? "Pricelist saved successfully" : ApiErrors.formatErrors(result.errors),
    errors: result.ok ? undefined : result.errors,
  };
}

export async function PutDateStrategies(
    request: DateStrategyRequest,
    divisionId: number
): Promise<{ ok: boolean; message: string; errors?: string[] }> {
  const localApi = await getLocalApi();
  const endpoint = apiConfig.edit_product_offerings_update_active_dates.replace(
      "{divisionId}",
      divisionId.toString()
  );

  const response = await localApi.call(endpoint, "PUT", JSON.stringify(request));
  const result = await processApiResponse(response, "Failed to save date strategies");

  return {
    ok: result.ok,
    message: result.ok ? "Date strategies saved successfully" : ApiErrors.formatErrors(result.errors),
    errors: result.ok ? undefined : result.errors,
  };
}

export async function DeleteDateStrategies(
    request: DeleteDateStrategyRequest,
    divisionId: number
): Promise<{ ok: boolean; message: string; errors?: string[] }> {
  const localApi = await getLocalApi();
  const endpoint = (apiConfig as any).edit_product_offerings_delete_date_strategy.replace(
      "{divisionId}",
      divisionId.toString()
  );

  const response = await localApi.call(endpoint, "DELETE", JSON.stringify(request));
  const result = await processApiResponse(response, "Failed to delete date strategy");

  return {
    ok: result.ok,
    message: result.ok ? "Date strategy deleted successfully" : ApiErrors.formatErrors(result.errors),
    errors: result.ok ? undefined : result.errors,
  };
}

export async function Decommission(
    request: DecommissionProductOffering,
    divisionId: number
): Promise<{ ok: boolean; message: string; errors?: string[] }> {
  const localApi = await getLocalApi();
  const endpoint = apiConfig.edit_product_offerings_decommission.replace(
      "{divisionId}",
      divisionId.toString()
  );

  const response = await localApi.call(endpoint, "PUT", JSON.stringify(request));
  const result = await processApiResponse(response, "Failed to decommission product offering");

  return {
    ok: result.ok,
    message: result.ok ? "Product offering decommissioned successfully" : ApiErrors.formatErrors(result.errors),
    errors: result.ok ? undefined : result.errors,
  };
}

export async function Recommission(
    request: RecommissionProductOffering,
    divisionId: number
): Promise<{ ok: boolean; message: string; errors?: string[] }> {
  const localApi = await getLocalApi();
  const endpoint = apiConfig.edit_product_offerings_recommission.replace(
      "{divisionId}",
      divisionId.toString()
  );

  const response = await localApi.call(endpoint, "PUT", JSON.stringify(request));
  const result = await processApiResponse(response, "Failed to recommission product offering");

  return {
    ok: result.ok,
    message: result.ok ? "Product offering recommissioned successfully" : ApiErrors.formatErrors(result.errors),
    errors: result.ok ? undefined : result.errors,
  };
}

export async function DeactivateOfferings(
    request: DecommissionProductOffering,
    divisionId: number
): Promise<{ ok: boolean; message: string; errors?: string[] }> {
  return Decommission(request, divisionId);
}

export async function ActivateOfferings(
    request: RecommissionProductOffering,
    divisionId: number
): Promise<{ ok: boolean; message: string; errors?: string[] }> {
  return Recommission(request, divisionId);
}

export async function DeleteOffering(
    request: DeleteOfferingsRequest,
    divisionId: number
): Promise<{ ok: boolean; message: string; errors?: string[] }> {
  const localApi = await getLocalApi();
  const endpoint = (apiConfig as any).edit_product_offerings_delete.replace(
      "{divisionId}",
      divisionId.toString()
  );

  const response = await localApi.call(endpoint, "PUT", JSON.stringify(request));
  const result = await processApiResponse(response, "Failed to delete offering");

  return {
    ok: result.ok,
    message: result.ok ? "Offering deleted successfully" : ApiErrors.formatErrors(result.errors),
    errors: result.ok ? undefined : result.errors,
  };
}

export async function ExcludeProductOfferingForBookingSystems(
    request: ExcludeProductOffering,
    divisionId: number
): Promise<{ ok: boolean; message: string; errors?: string[] }> {
  const localApi = await getLocalApi();
  const endpoint = apiConfig.edit_product_offerings_exclude.replace(
      "{divisionId}",
      divisionId.toString()
  );

  try {
    const response = await localApi.call(endpoint, "PUT", JSON.stringify(request));
    const result = await processApiResponse(response, "Failed to exclude product offering");

    return {
      ok: result.ok,
      message: result.ok ? "Product offering excluded successfully" : ApiErrors.formatErrors(result.errors),
      errors: result.ok ? undefined : result.errors,
    };
  } catch (err) {
    const errors = ApiErrors.extractAllErrors(err, undefined, "Failed to exclude product offering");
    return { ok: false, message: ApiErrors.formatErrors(errors), errors };
  }
}
